import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Sparkles, ChevronRight, ArrowRight } from 'lucide-react';
import Seo from '@/components/shared/Seo';
import Breadcrumbs from '@/components/shared/Breadcrumbs';
import { hubs, countryHubs } from '@/data/hubs/hubs';
import { combineSchemas, organizationSchema, websiteSchema, webPageSchema, breadcrumbSchema, faqSchema } from '@/lib/schemaOrg';
import { trackStudyAbroadEnquiry } from '@/lib/analytics';

// Hub slugs that map to the study-abroad conversion funnel.
const STUDY_ABROAD_HUBS = new Set([
  'study-abroad',
  'study-in-australia',
  'study-in-south-korea',
  'university-admissions',
  'student-visa-guidance',
]);

/** HubPage renders any of the /hubs entries. */
const HubPage = ({ hubKey }) => {
  const params = useParams();
  const key = hubKey || params.hubKey || params.slug;
  const hub = hubs[key] || countryHubs[key] || null;

  if (!hub) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6 text-center">
        <div>
          <div className="text-2xl font-bold">This hub is being prepared.</div>
          <p className="mt-2 text-gray-600">Please explore <Link to="/" className="text-red-700 underline">Winhouse Tutors</Link> instead.</p>
        </div>
      </div>
    );
  }

  const path = `/${hub.slug}`;
  const bcItems = [{ name: hub.h1 || hub.title.split('|')[0].trim(), path }];

  const schemas = combineSchemas(
    organizationSchema(),
    websiteSchema(),
    webPageSchema({ title: hub.title, description: hub.metaDescription, path }),
    breadcrumbSchema(bcItems),
    hub.faqs && hub.faqs.length ? faqSchema(hub.faqs) : null,
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid={`hub-page-${hub.slug}`}>
      <Seo
        title={hub.title}
        description={hub.metaDescription}
        path={path}
        jsonLd={schemas}
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Breadcrumbs items={bcItems} />

        <div className="mt-6 mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-700 font-bold flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Winhouse Tutors
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black text-black leading-tight" data-testid="hub-h1">
            {hub.h1}
          </h1>
          <p className="mt-4 text-lg text-gray-700 leading-relaxed max-w-3xl">{hub.lede}</p>

          <div className="mt-6 flex flex-wrap gap-3" data-testid="hub-ctas">
            {(hub.ctas || []).map((c, i) => (
              <Link key={i} to={c.href}
                onClick={() => {
                  if (STUDY_ABROAD_HUBS.has(hub.slug)) {
                    try { trackStudyAbroadEnquiry(hub.slug, c.label); } catch { /* noop */ }
                  }
                }}
                className="h-11 px-6 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5"
                data-testid={`hub-cta-${i}`}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>

        {/* Sections */}
        {(hub.sections || []).map((s, i) => (
          <section key={i} className="mb-8" data-testid={`hub-section-${i}`}>
            <h2 className="text-2xl font-bold text-black">{s.heading}</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">{s.body}</p>
          </section>
        ))}

        {/* Market points for country pages */}
        {hub.marketPoints && (
          <section className="mb-8" data-testid="hub-market-points">
            <h2 className="text-2xl font-bold text-black">Why students in {hub.country} choose Winhouse</h2>
            <ul className="mt-3 space-y-2">
              {hub.marketPoints.map((p, i) => (
                <li key={i} className="pl-6 relative text-gray-700 leading-relaxed">
                  <ChevronRight className="h-4 w-4 text-red-700 absolute left-0 top-1" /> {p}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* FAQ */}
        {hub.faqs && hub.faqs.length > 0 && (
          <section className="mb-8" data-testid="hub-faqs">
            <h2 className="text-2xl font-bold text-black">Frequently asked questions</h2>
            <div className="mt-3 divide-y divide-gray-200 border border-gray-200 rounded-2xl bg-white">
              {hub.faqs.map((f, i) => (
                <details key={i} className="p-4 group" data-testid={`hub-faq-${i}`}>
                  <summary className="cursor-pointer text-sm font-bold text-black flex items-center justify-between gap-2">
                    {f.q}
                    <ChevronRight className="h-4 w-4 text-gray-400 group-open:rotate-90 transition" />
                  </summary>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Related hubs */}
        {hub.relatedHubs && hub.relatedHubs.length > 0 && (
          <section className="mb-8" data-testid="hub-related">
            <h2 className="text-lg font-bold text-black">Related programmes</h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {hub.relatedHubs.map((rk) => {
                const r = hubs[rk]; if (!r) return null;
                return (
                  <Link key={rk} to={`/${rk}`} className="p-4 rounded-xl border border-gray-200 bg-white hover:border-red-300"
                    data-testid={`hub-related-${rk}`}>
                    <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Explore</div>
                    <div className="text-sm font-bold text-black mt-1">{r.h1.split('—')[0]}</div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="mt-10 bg-black text-white rounded-2xl p-6 sm:p-8" data-testid="hub-bottom-cta">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D3D86F] font-bold">Winhouse Tutors</div>
          <h3 className="mt-2 text-2xl font-black">Ready to start?</h3>
          <p className="mt-2 text-white/70 text-sm max-w-2xl">Tell us what you are preparing for. We&rsquo;ll match a shortlisted tutor and set up a low-commitment trial lesson within one business day.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/get-started" className="h-11 px-6 rounded-full bg-[#D3D86F] hover:bg-[#c4ca5e] text-black text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5" data-testid="hub-bottom-cta-get-started">Get Started</Link>
            <Link to="/tutors" className="h-11 px-6 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5" data-testid="hub-bottom-cta-find-tutor">Find a Tutor</Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HubPage;
